import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Loader2, X } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { relabelBranch, indexBranches, fullName, fmtMoney } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import { recurringTypeTone, recurringProgress, collectedFor, indexCollected } from '../../lib/recurring.js'
import RecurringEditor from '../../components/employee/RecurringEditor.jsx'
import Pager from '../../components/Pager.jsx'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'

const PER_PAGE = 12
const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

const STATUS_OPTS = [
  { key: 'active', label: 'ค้างอยู่' },
  { key: 'done', label: 'ครบแล้ว' },
  { key: '', label: 'ทั้งหมด' },
]

// Compact per-entry progress row shown on the employee card.
function EntryLine({ entry, collected }) {
  const p = recurringProgress(entry, collected)
  const barTone = p.done ? 'bg-emerald-500' : 'bg-brand-500'
  return (
    <div className="py-2 first:pt-0 last:pb-0">
      <div className="flex items-center gap-2 text-[13px]">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${recurringTypeTone(entry.type)}`}>{entry.type || 'อื่นๆ'}</span>
        <span className="text-slate-500 truncate">{entry.remark || ''}</span>
        <span className="ml-auto tabular-nums font-medium text-slate-700">{p.collected}/{p.total} งวด</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barTone}`} style={{ width: `${p.pct}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400 tabular-nums">
        <span>งวดละ {fmtMoney(p.amountPer)}</span>
        <span>{p.done ? 'ครบแล้ว' : `เหลือ ${fmtMoney(p.amountRemaining)}`}</span>
      </div>
    </div>
  )
}

export default function EmployeeRecurring() {
  const [branches, setBranches] = useState([])
  const [all, setAll] = useState([])            // every employee with recurring[] (fetched once per load)
  const [collectedMap, setCollectedMap] = useState(new Map()) // uuid → {collected, amount}
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [editor, setEditor] = useState(null)    // { employeeId } | { employeeId: null } for the picker flow
  const reqId = useRef(0)

  // Shared with every พนักงาน tab — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter()
  const [branchId, setBranchId] = usePersistedState('emp:rc:branch', '')
  const [status, setStatus] = usePersistedState('emp:rc:status', 'active') // active | done | '' (all)
  const [page, setPage] = useState(1)

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const branchName = useMemo(() => indexBranches(branches), [branches])

  function load() {
    const id = ++reqId.current
    setLoading(true); setErr('')
    // The recurring case has no branch/search filter server-side, so fetch the
    // (small) full set of employees-with-entries once and filter on the client.
    // Two reads: the recurring master (Employee.recurring[]) and the authoritative
    // collected counts (salary_em aggregated by recurring_uuid — the master's own
    // salary_em_id[] mirror is empty, which is why progress used to read 0/N).
    Promise.all([
      apiGet('/api/hr/employees', { type: 'recurring', per_page: 200 }),
      apiGet('/api/hr/employee-recurring-collected').catch(() => ({ items: [] })),
    ])
      .then(([emp, coll]) => {
        if (id !== reqId.current) return
        const items = Array.isArray(emp) ? emp : (emp?.items || [])
        setAll(items.filter((e) => Array.isArray(e.recurring) && e.recurring.length > 0))
        setCollectedMap(indexCollected(Array.isArray(coll) ? coll : (coll?.items || [])))
      })
      .catch((e) => { if (id === reqId.current) setErr(String(e.message || e)) })
      .finally(() => { if (id === reqId.current) setLoading(false) })
  }
  useEffect(load, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1) }, [employeeId, branchId, status])

  // An entry is "complete" when its collected installments reach N.
  const entryDone = (entry) => recurringProgress(entry, collectedFor(collectedMap, entry)).done
  const matchStatus = (entry) => (status === 'done' ? entryDone(entry) : status === 'active' ? !entryDone(entry) : true)

  // Each employee keeps only the entries matching the completeness filter; those
  // with none left drop out of the list entirely.
  const filtered = useMemo(() => {
    return all
      .map((e) => ({ ...e, _entries: (Array.isArray(e.recurring) ? e.recurring : []).filter(matchStatus) }))
      .filter((e) => {
        if (!e._entries.length) return false
        if (branchId && Number(e.branch_id) !== Number(branchId)) return false
        if (employeeId && String(e.id) !== String(employeeId)) return false
        return true
      })
  }, [all, employeeId, branchId, status, collectedMap]) // eslint-disable-line react-hooks/exhaustive-deps

  const pageTotal = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filtered, page],
  )

  const hasFilter = employeeId || branchId
  const relabel = (id) => relabelBranch(branchName.get(Number(id)) || '') || '—'

  return (
    <div className="max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-5">
        <p className="text-[13.5px] text-slate-500">
          รายการหักเงินแบบผ่อนชำระ (ค่าฝึกอบรม / เงินกู้) — ระบบจะหักในสลิปเงินเดือนจนกว่าจะครบจำนวนงวด · คลิกการ์ดเพื่อจัดการรายการ
        </p>
        <button
          onClick={() => setEditor({ employeeId: null })}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm shrink-0"
        >
          <Plus size={16} /> เพิ่มรายการหักประจำ
        </button>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        {/* activeOnly={false}: an ex-employee can still owe installments, and the old
            text search found them — the picker must too. */}
        <EmployeePicker
          value={employeeId} onChange={setEmployeeId} allowAll activeOnly={false}
          className="flex-1 min-w-[220px] max-w-[320px]"
        />
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        {hasFilter && (
          <button
            onClick={() => { setEmployeeId(''); setBranchId('') }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100"
          >
            <X size={14} /> ล้าง
          </button>
        )}
        {/* completeness filter — defaults to ค้างอยู่ (non-complete) */}
        <div className="ml-auto inline-flex rounded-xl ring-1 ring-slate-200 bg-white p-0.5">
          {STATUS_OPTS.map((o) => (
            <button key={o.key} onClick={() => setStatus(o.key)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition ${
                status === o.key ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* content */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-slate-400">
          <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
        </div>
      ) : err ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-rose-500">{err}</div>
      ) : !filtered.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-slate-400">
          {all.length ? 'ไม่พบรายการที่ตรงกับตัวกรอง' : 'ยังไม่มีพนักงานที่มีรายการหักประจำ — กด “เพิ่มรายการหักประจำ” เพื่อเริ่ม'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pageRows.map((e) => (
            <button
              key={e.id}
              onClick={() => setEditor({ employeeId: e.id })}
              className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-brand-300 hover:shadow-sm transition"
            >
              <div className="flex items-center gap-2.5 mb-3">
                {/* The CARD opens the deduction schedule; the avatar+name opens the
                    person. A span, not a button — this sits inside the card's own
                    <button>, and its stopPropagation is what keeps the two apart. */}
                <EmployeeTrigger employeeId={e.id} className="items-center gap-2.5">
                  <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 text-[13px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-brand-100">
                    {nameInitial(e.nickname || e.name || e.code || '?')}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate tabular-nums">{e.code || '—'}</div>
                    <div className="text-[12px] text-slate-400 truncate">{[fullName(e), relabel(e.branch_id)].filter(Boolean).join(' · ')}</div>
                  </div>
                </EmployeeTrigger>
                <span className="ml-auto text-[11.5px] text-slate-400 shrink-0">{e._entries.length} รายการ</span>
              </div>
              <div className="divide-y divide-slate-100">
                {e._entries.map((entry, i) => (
                  <EntryLine key={entry.uuid || i} entry={entry} collected={collectedFor(collectedMap, entry)} />
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* footer / paging */}
      <div className="flex items-center justify-between mt-4 text-[13px] text-slate-500">
        <span>ทั้งหมด {filtered.length.toLocaleString()} คน</span>
        <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
      </div>

      {editor && (
        <RecurringEditor
          employeeId={editor.employeeId}
          branches={branches}
          onClose={() => setEditor(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}
